// Features: M004-F0795 (WebUI #263, slice B1a: the drawer shell; credit mode/display and effective-from are the only editable parts)
// Tests: T-M004-0412 (host tag visibility, LedgerAccountPage.billingProfileTag.test.js), T-M004-0418 (unknown outcome),
//        T-M004-0422 (reason gating), T-M004-0423 (step-up retry, same key), T-M004-0424 (revision conflict keeps edits)
//
// Opened from the customer's Ledger page for a ledger_manage caller only (the host decides). Reads customer_billing_profile.get,
// shows a read-only summary, and edits credit mode/display and effective-from. The groups and apportionments editors are later slices.
import React, { useEffect, useRef, useState } from 'react';
import { customerBillingProfileApi } from '../../../services/customerBillingProfileApi';
import { errorCode, READ_CONDITIONS } from '../../../services/billingProfileAdapter';
import { classifyRelayOutcome } from '../../../services/relayOutcome';
import { getStepUpChallengeFromError } from '../../../utils/stepUp';
import StepUpDialog from '../../auth/StepUpDialog';
import GroupsEditor, { groupsForBody, rowsFromGroups } from './GroupsEditor';
import ApportionmentEditor, { rowsFromRules, rulesForBody } from './ApportionmentEditor';
import BillingProfileHistory from './BillingProfileHistory';
import {
  apportionmentText, effectiveFromFor, FIELD_INVALID, londonDateOf, refusalPath, refusalSentence, summariseSave, timeWindowText,
  READ_FAILED, validateProfileDraft, validateReason,
} from './billingProfileModel';

export const SAVED_SLOW_SENTENCE = 'Saved (confirmation was slow).';
export const NOT_SAVED_SENTENCE = 'Not saved. Please retry.';
export const UNCONFIRMED_SENTENCE = "We couldn't confirm whether this was saved. Reload to check.";
export const CANCELLED_SENTENCE = 'Verification was cancelled. Your edits are kept.';
export const NEWER_REVISION_NOTE = 'A newer revision of this profile exists. Your edits are kept; supersede it deliberately by saving again.';
const ORG_DEFAULT = '__org__';
const ORIGIN_LABEL = { customer: 'Customer setting', org: 'Organisation default', default: 'Default' };
const MODE_LABEL = { auto_on_invoice: 'Automatic on invoice', customer_controlled: 'Customer-controlled' };
const DISPLAY_LABEL = { on_invoice: 'Shown on the invoice', statement_only: 'Statement only' };

const newKey = () => globalThis.crypto?.randomUUID?.() || `bp-${Date.now()}-${Math.random()}`;

const londonDate = (iso) => new Date(iso).toLocaleDateString('en-GB', { timeZone: 'Europe/London', day: 'numeric', month: 'long', year: 'numeric' });
const tomorrowLondon = () => {
  const [y, m, d] = londonDateOf().split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + 1)).toISOString().slice(0, 10);
};

// Read failures: the billing settings page's sentences for the coded conditions; anything else is the generic sentence.
export const readSentence = (cause) => {
  const code = errorCode(cause);
  if (READ_CONDITIONS[code]) return READ_CONDITIONS[code];
  if (code === 'LEDGER_ACCOUNT_PERMISSION_DENIED') return refusalSentence(cause);
  return READ_FAILED;
};

// The request body from the operator's edits. creditDraft: undefined = untouched (not sent), null = reset to org default.
// groups: undefined = untouched (not sent); otherwise the whole list, which REPLACES the saved groups. apportionments: the same rule.
// Throws the sentence for a past or today date (effectiveFromFor), so the caller shows it on the date field.
export function buildSetBody({ profile, reason, creditDraft, timing, date, now, groups, apportionments }) {
  const body = { expected_rev: profile.rev, reason: reason.trim() };
  const credit = {};
  if (creditDraft.mode !== undefined) credit.mode = creditDraft.mode;
  if (creditDraft.display !== undefined) credit.display = creditDraft.display;
  if (Object.keys(credit).length) body.credit = credit;
  const effective = effectiveFromFor(timing, date, now);
  if (effective !== undefined) body.effective_from = effective;
  if (groups !== undefined) body.groups = groups;
  if (apportionments !== undefined) body.apportionments = apportionments;
  return body;
}

// Did a fresh read show the save this body sent? Used only for an unknown outcome (§7.2): the rev moved by one and every member sent matches.
export function savedMatches(fresh, body) {
  if (!fresh || fresh.rev !== body.expected_rev + 1) return false;
  const target = body.effective_from ? fresh.pending : fresh;
  if (!target) return false;
  const credit = body.credit || {};
  return Object.entries(credit).every(([field, value]) => (value === null
    ? target.credit?.[`${field}_origin`] !== 'customer'
    : target.credit?.[field] === value));
}

const originText = (value, origin, labels) => {
  const shown = value ? labels[value] : 'Organisation default';
  return `${shown} (${ORIGIN_LABEL[origin] || 'Default'})`;
};

export default function BillingProfileDrawer({ orgId, userId, customerName, onClose }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [readError, setReadError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);
  const [editing, setEditing] = useState(false);
  const [creditDraft, setCreditDraft] = useState({});
  const [timing, setTiming] = useState('now');
  const [date, setDate] = useState('');
  const [dateError, setDateError] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [notice, setNotice] = useState('');
  const [summary, setSummary] = useState(null);
  const [stepUp, setStepUp] = useState(null);
  const [groupsDraft, setGroupsDraft] = useState(null); // editor rows while editing; null = no edit made
  const [apptDraft, setApptDraft] = useState(null); // apportionment rows while editing; null = no edit made
  const [serverFieldErrors, setServerFieldErrors] = useState({}); // { path: sentence } from a LEDGER_PROFILE_INVALID refusal
  const [showHistory, setShowHistory] = useState(false); // M004-F0798: the history panel replaces the summary while open
  const attempt = useRef(null); // { signature, key }: one key per Save intent, reused by its retries
  const pendingRetry = useRef(null); // { body, key } held while the step-up dialog is open
  const reasonProblem = validateReason(reason);

  const fetchProfile = async () => {
    try {
      const res = await customerBillingProfileApi.get(orgId, userId);
      return { ok: true, data: res?.data?.data };
    } catch (cause) {
      return { ok: false, cause };
    }
  };

  useEffect(() => {
    let current = true;
    setLoading(true);
    setReadError('');
    fetchProfile().then((result) => {
      if (!current) return;
      setLoading(false);
      if (result.ok) setProfile(result.data);
      else setReadError(readSentence(result.cause));
    });
    return () => { current = false; };
  }, [orgId, userId, reloadToken]);

  const resetEdits = () => {
    setEditing(false); setCreditDraft({}); setTiming('now'); setDate(''); setDateError(''); setReason('');
    setGroupsDraft(null); setApptDraft(null); setServerFieldErrors({});
  };

  // The groups sent with a save: only when the draft differs from the saved list (compared through the same mapping).
  const groupsToSend = (profileNow) => {
    if (!groupsDraft) return undefined;
    const next = groupsForBody(groupsDraft);
    return JSON.stringify(next) === JSON.stringify(groupsForBody(rowsFromGroups(profileNow.groups))) ? undefined : next;
  };

  // The apportionments sent with a save: only when the draft differs from the saved rules (the same mapping on both sides).
  const apportionmentsToSend = (profileNow) => {
    if (!apptDraft) return undefined;
    const next = rulesForBody(apptDraft);
    return JSON.stringify(next) === JSON.stringify(rulesForBody(rowsFromRules(profileNow.apportionments))) ? undefined : next;
  };

  // Unknown outcome (§7.2): re-read and compare. Returns the sentence to show ('' when the save is confirmed).
  const confirmUnknown = async (body) => {
    const fresh = await fetchProfile();
    if (!fresh.ok) return UNCONFIRMED_SENTENCE;
    setProfile(fresh.data);
    if (savedMatches(fresh.data, body)) {
      attempt.current = null;
      resetEdits();
      setNotice(SAVED_SLOW_SENTENCE);
      return '';
    }
    return NOT_SAVED_SENTENCE;
  };

  const send = async (body, key, retried) => {
    setSaving(true); setSaveError(''); setNotice('');
    try {
      const res = await customerBillingProfileApi.set(orgId, { user_id: userId, ...body }, key);
      attempt.current = null;
      const reply = res?.data?.data || {};
      setSummary({ rev: reply.profile?.rev, pendingFrom: reply.profile?.pending?.effective_from || null, lines: summariseSave(reply) });
      if (reply.profile) setProfile(reply.profile);
      resetEdits();
      const fresh = await fetchProfile();
      if (fresh.ok) setProfile(fresh.data);
    } catch (cause) {
      const status = Number(cause?.response?.status);
      const challenge = status === 403 ? null : getStepUpChallengeFromError(cause);
      if (challenge?.id && !retried) {
        pendingRetry.current = { body, key };
        setStepUp(challenge);
      } else if (errorCode(cause) === 'LEDGER_PROFILE_REVISION_CONFLICT') {
        // Re-read and keep the operator's edits (§7.1). Nothing is overwritten; a new Save is a new action with a fresh rev.
        const fresh = await fetchProfile();
        if (fresh.ok) setProfile(fresh.data);
        setSaveError(`${refusalSentence(cause)} ${NEWER_REVISION_NOTE}`);
      } else if (errorCode(cause) === 'LEDGER_PROFILE_INVALID' && refusalPath(cause)?.startsWith('groups')) {
        // The Ledger's field (details.path) gets the generic sentence on its own field; the server message is never shown (§7).
        setServerFieldErrors({ [refusalPath(cause)]: FIELD_INVALID });
        setSaveError('');
      } else if (classifyRelayOutcome(cause) === 'unknown') {
        setSaveError(await confirmUnknown(body));
      } else {
        setSaveError(refusalSentence(cause));
      }
    } finally {
      setSaving(false);
    }
  };

  const onSave = async (event) => {
    event.preventDefault();
    if (saving || !profile || reasonProblem || draftErrors.length) return;
    let body;
    try {
      body = buildSetBody({
        profile, reason, creditDraft, timing, date, now: new Date(), groups: groupsToSend(profile), apportionments: apportionmentsToSend(profile),
      });
      setDateError('');
    } catch (error) {
      setDateError(error.message);
      return;
    }
    const signature = JSON.stringify(body);
    if (!attempt.current || attempt.current.signature !== signature) attempt.current = { signature, key: newKey() };
    await send(body, attempt.current.key, false);
  };

  const onStepUpComplete = () => {
    const retry = pendingRetry.current;
    setStepUp(null);
    if (retry) send(retry.body, retry.key, true); // the SAME body and the SAME key
  };

  const onStepUpCancel = () => {
    pendingRetry.current = null;
    setStepUp(null);
    setSaveError(CANCELLED_SENTENCE);
  };

  const credit = profile?.credit || {};
  const modeValue = creditDraft.mode === undefined ? (credit.mode_origin === 'customer' ? credit.mode : ORG_DEFAULT)
    : (creditDraft.mode === null ? ORG_DEFAULT : creditDraft.mode);
  const displayValue = creditDraft.display === undefined ? (credit.display_origin === 'customer' ? (credit.display || ORG_DEFAULT) : ORG_DEFAULT)
    : (creditDraft.display === null ? ORG_DEFAULT : creditDraft.display);
  const groups = Array.isArray(profile?.groups) ? profile.groups.slice(0, 12) : [];
  const rules = Array.isArray(profile?.apportionments) ? profile.apportionments.slice(0, 8) : [];
  // Local validation (UX mirror, the Ledger judges): while editing, Save is disabled while any rule fails.
  const draftErrors = editing && profile && groupsDraft
    ? validateProfileDraft({ groups: groupsForBody(groupsDraft), apportionments: rulesForBody(apptDraft || []) }) : [];

  const renderSummary = () => (
    <section className="bps-section" aria-labelledby="bp-summary-heading">
      <h3 id="bp-summary-heading">Current billing profile</h3>
      <p data-testid="billing-profile-state">{profile.rev === 0 ? 'Default (no profile saved)' : `Custom, revision ${profile.rev}`}</p>
      {profile.pending ? <p data-testid="billing-pending">Pending from {londonDate(profile.pending.effective_from)}</p> : null}
      <p>Effective from: {profile.effective_from ? londonDate(profile.effective_from) : 'Now'}</p>
      {profile.updated_by ? <p>Last changed by {profile.updated_by} at {londonDate(profile.updated_at)}</p> : null}
      <p><button type="button" className="bps-secondary" data-testid="billing-history-open" onClick={() => setShowHistory(true)}>History</button></p>
      <h4>Credit</h4>
      <p data-testid="billing-credit-mode">Mode: {originText(credit.mode, credit.mode_origin, MODE_LABEL)}</p>
      <p data-testid="billing-credit-display">Display: {originText(credit.display, credit.display_origin, DISPLAY_LABEL)}</p>
      <h4>Groups</h4>
      {groups.length ? <ul>{groups.map((g) => (
        <li key={g.group_key}>{g.label}: {timeWindowText(g.match?.start_time_from, g.match?.start_time_to)}; bill to {g.bill_to?.kind === 'contact' ? 'a contact' : 'the customer'}</li>
      ))}</ul> : <p>No groups.</p>}
      <h4>Apportionments</h4>
      {rules.length ? <ul>{rules.map((r) => <li key={r.rule_id}>{r.label}: {apportionmentText(r, profile.groups)}</li>)}</ul> : <p>No apportionments.</p>}
    </section>
  );

  const renderEditor = () => (
    <section className="bps-section" aria-labelledby="bp-edit-heading" data-testid="billing-edit-form">
      <h3 id="bp-edit-heading">Edit billing profile</h3>
      <fieldset>
        <legend>Credit mode</legend>
        <p>Current: {originText(credit.mode, credit.mode_origin, MODE_LABEL)}</p>
        <label>Credit mode
          <select aria-label="Credit mode" value={modeValue} onChange={(e) => setCreditDraft((d) => ({ ...d, mode: e.target.value === ORG_DEFAULT ? null : e.target.value }))}>
            <option value={ORG_DEFAULT}>Organisation default</option>
            <option value="auto_on_invoice">Automatic on invoice</option>
            <option value="customer_controlled">Customer-controlled</option>
          </select>
        </label>
        <button type="button" onClick={() => setCreditDraft((d) => ({ ...d, mode: null }))}>Reset to organisation default</button>
      </fieldset>
      <fieldset>
        <legend>Credit display</legend>
        <p>Current: {originText(credit.display, credit.display_origin, DISPLAY_LABEL)}</p>
        <label>Credit display
          <select aria-label="Credit display" value={displayValue} onChange={(e) => setCreditDraft((d) => ({ ...d, display: e.target.value === ORG_DEFAULT ? null : e.target.value }))}>
            <option value={ORG_DEFAULT}>Organisation default</option>
            <option value="on_invoice">Shown on the invoice</option>
            <option value="statement_only">Statement only</option>
          </select>
        </label>
        <button type="button" onClick={() => setCreditDraft((d) => ({ ...d, display: null }))}>Reset to organisation default</button>
      </fieldset>
      <GroupsEditor
        orgId={orgId}
        rows={groupsDraft || []}
        onChange={(next) => { setGroupsDraft(next); setServerFieldErrors({}); }}
        errors={draftErrors}
        serverErrors={serverFieldErrors}
      />
      <ApportionmentEditor
        orgId={orgId}
        userId={userId}
        rows={apptDraft || []}
        groups={groupsForBody(groupsDraft || [])}
        onChange={(next) => { setApptDraft(next); setServerFieldErrors({}); }}
        errors={draftErrors}
        serverErrors={serverFieldErrors}
      />
      <fieldset>
        <legend>Effective from</legend>
        <label><input type="radio" name="bp-timing" value="now" checked={timing === 'now'} onChange={() => setTiming('now')} /> Now</label>
        <label><input type="radio" name="bp-timing" value="future" checked={timing === 'future'} onChange={() => setTiming('future')} /> A date from tomorrow</label>
        {timing === 'future' ? (
          <label>Date
            <input type="date" min={tomorrowLondon()} value={date} onChange={(e) => { setDate(e.target.value); setDateError(''); }} />
          </label>
        ) : null}
        <p>Applies to unissued invoices and to lines that land after the save. Issued invoices never change.</p>
        {dateError ? <p role="alert" data-testid="billing-date-error">{dateError}</p> : null}
      </fieldset>
      <label>Reason for this change
        <input type="text" value={reason} onChange={(e) => setReason(e.target.value)} aria-describedby="bp-reason-hint" autoComplete="off" />
      </label>
      <p id="bp-reason-hint" data-testid="billing-reason-hint">{reasonProblem || 'Reason accepted.'}</p>
    </section>
  );

  const renderBody = () => {
    if (loading) return <p role="status" aria-busy="true" className="bps-skeleton">Loading billing profile…</p>;
    if (readError) {
      return (
        <div role="alert">
          <p>{readError}</p>
          <button type="button" className="bps-secondary" onClick={() => setReloadToken((t) => t + 1)}>Retry</button>
        </div>
      );
    }
    if (!profile) return null;
    return (
      <>
        {showHistory ? <BillingProfileHistory orgId={orgId} userId={userId} onBack={() => setShowHistory(false)} /> : null}
        {!showHistory && summary ? (
          <section className="bps-section" role="status" data-testid="billing-save-summary">
            <p>Saved as revision {summary.rev}.{summary.pendingFrom ? ` Pending from ${londonDate(summary.pendingFrom)}.` : ''}</p>
            {summary.lines.map((line) => <p key={line}>{line}</p>)}
          </section>
        ) : null}
        {notice ? <p role="status" data-testid="billing-notice">{notice}</p> : null}
        {!showHistory ? renderSummary() : null}
        {editing && !showHistory ? renderEditor() : null}
        {saveError ? <p role="alert" data-testid="billing-save-error">{saveError}</p> : null}
      </>
    );
  };

  return (
    <>
      <StepUpDialog
        challenge={stepUp}
        title="Verify billing profile change"
        description="Changing a customer's billing profile needs a fresh step-up (SAL 4). Your save will replay automatically once verified."
        onComplete={onStepUpComplete}
        onCancel={onStepUpCancel}
      />
      <div className="bps-backdrop" aria-hidden="true" onClick={onClose} />
      <aside className="bps-drawer" role="dialog" aria-modal="false" aria-labelledby="bp-drawer-title" data-testid="billing-profile-drawer"
        onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}>
        <form onSubmit={onSave} noValidate aria-busy={saving || loading ? 'true' : undefined}>
          <header className="bps-drawer-head">
            <h2 id="bp-drawer-title">Billing profile{customerName ? `: ${customerName}` : ''}</h2>
            <button type="button" className="bps-close" onClick={onClose} aria-label="Close">×</button>
          </header>
          <div className="bps-drawer-body">{renderBody()}</div>
          <footer className="bps-drawer-foot">
            {profile && !editing && !showHistory ? (
              <button type="button" className="bps-secondary" data-testid="billing-edit"
                onClick={() => { setGroupsDraft(rowsFromGroups(profile.groups)); setApptDraft(rowsFromRules(profile.apportionments)); setEditing(true); }}>Edit</button>
            ) : null}
            {profile && editing ? (
              <button type="submit" className="bps-primary" data-testid="billing-profile-save" disabled={saving || Boolean(reasonProblem) || draftErrors.length > 0}>
                {saving ? 'Saving...' : 'Save'}
              </button>
            ) : null}
            {profile && editing ? <button type="button" className="bps-secondary" onClick={resetEdits} disabled={saving}>Discard edits</button> : null}
          </footer>
        </form>
      </aside>
    </>
  );
}
